import { AUTH_USER_KEY, ROLE_KEY } from "./constants";

export type AuthUser = {
  id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
  role: string;
  business_id?: number | string;
  businesses?: Array<{
    business_id: number | string;
    role: string;
    business_trial?: boolean;
    business_trial_ends_at?: string | null;
    business_trial_active?: boolean;
    business_trial_expired?: boolean;
    business_trial_days_left?: number | null;
  }>;
  impersonated_by_name?: string;
};

const PROD_API_BASE_URL = "https://api.sonne-qm.de";
const envApiBase = (import.meta.env?.VITE_API_BASE_URL as string | undefined)?.trim();
const isLocalUrl = (url?: string): boolean => !!url && /(localhost|127\.0\.0\.1|0\.0\.0\.0)/i.test(url);

// In Produktion NIEMALS auf eine lokale Backend-URL zeigen. So kann eine
// versehentlich in den Build geratene VITE_API_BASE_URL (z. B. aus dem
// Backend-.env: http://127.0.0.1:3000) den Login nicht mehr kaputtmachen.
export const API_BASE_URL = (
  import.meta.env?.DEV
    ? (envApiBase || "http://localhost:3000")
    : (envApiBase && !isLocalUrl(envApiBase) ? envApiBase : PROD_API_BASE_URL)
).replace(/\/$/, "");

export const apiUrl = (path: string) => `${API_BASE_URL}${path}`;

export const slugify = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

// --- Native App (Capacitor): Bearer-Token statt HttpOnly-Cookie ---
// Im WebView (Origin capacitor://localhost) greift das Domain-Cookie der API
// nicht, daher wird der beim Login gelieferte JWT gespeichert und als
// "Authorization: Bearer" mitgesendet. Im Web wird KEIN Token gespeichert
// (dort bleibt das sichere HttpOnly-Cookie die Quelle der Wahrheit).
const TOKEN_KEY = "sonne_jwt_v1";

export const isNativeApp = (): boolean => {
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    return !!cap?.isNativePlatform?.();
  } catch {
    return false;
  }
};

export const setAuthToken = (token: string | null | undefined) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export const getAuthToken = (): string | null => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

export const saveAuth = (user: AuthUser) => {
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};

export const clearAuth = () => {
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem("sonne_business_selected_v1");
  localStorage.removeItem(TOKEN_KEY);
};

export const currentAuthUser = (): AuthUser | null => {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const headers = new Headers(init.headers || {});
  // Native App: JWT als Bearer mitsenden (nur dort ist ein Token gespeichert).
  const token = getAuthToken();
  if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  const fetchInit: RequestInit = { ...init, headers, credentials: "include" };
  if (fetchInit.cache == null) fetchInit.cache = "no-store";
  const resp = await fetch(input, fetchInit);
  if (resp.status === 401) {
    clearAuth();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("auth:expired"));
    }
  } else if (resp.status === 403 && typeof window !== "undefined") {
    // Probezeitraum-Sperren (Export/Import/Limit/abgelaufen) einheitlich melden.
    try {
      const body = await resp.clone().json();
      if (body && (body.error === "trial_restricted" || body.error === "trial_expired")) {
        const msg = Array.isArray(body.errors) && body.errors.length
          ? String(body.errors[0])
          : "Diese Funktion ist nur in der Vollversion verfügbar.";
        window.dispatchEvent(new CustomEvent("g:toast", { detail: { message: msg, variant: "warning" } }));
      }
    } catch {
      /* keine JSON-Antwort */
    }
  }
  return resp;
}

export async function logoutRequest() {
  try {
    await authFetch(apiUrl("/api/v1/auth/logout"), { method: "DELETE" });
  } catch (e) {
    console.error("Logout request failed", e);
  } finally {
    clearAuth();
  }
}
